import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { CopyToClipboard } from "Components/Shared/CopyToClipboard";
import type { RaffleRoundDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const RESOURCES = Resources.Admin.Raffle;

type Props = {
  isOpen: boolean;
  round: RaffleRoundDto | null;
  onClose: () => void;
};

type FingerprintProps = {
  label: string;
  value: string;
  copiedMessage: string;
  help: string;
};

// A close-time SHA-256 seal the operator copies into the draw protocol.
const Fingerprint = ({
  label,
  value,
  copiedMessage,
  help,
}: FingerprintProps) => (
  <Stack gap={0.5} mt={1}>
    <Typography variant="subtitle2">{label}</Typography>
    <Stack direction="row" alignItems="center">
      <Typography
        variant="body2"
        fontFamily="monospace"
        sx={{ wordBreak: "break-all" }}
      >
        {value}
      </Typography>
      <CopyToClipboard
        text={value}
        accessibilityLabel={label}
        successMessage={copiedMessage}
        size={18}
      />
    </Stack>
    <Typography variant="caption" color="text.secondary">
      {help}
    </Typography>
  </Stack>
);

/** Read-only view of a round in any status; the only reward view once the edit is locked. */
export const RaffleRoundViewDialog = ({ isOpen, round, onClose }: Props) => {
  const { t } = useResource();

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{round?.name}</DialogTitle>
      <DialogContent>
        {round && (
          <Stack gap={1} mt={1}>
            <Typography>
              {t(RESOURCES.Table.DrawDate)}:{" "}
              <b>{round.drawDate ? formatDate(round.drawDate) : "—"}</b>
            </Typography>
            {round.ticketCount != null && (
              <Typography>
                {t(RESOURCES.Table.Tickets)}: <b>{round.ticketCount}</b>
              </Typography>
            )}
            {round.exportChecksum && (
              <Fingerprint
                label={t(RESOURCES.Table.Checksum)}
                value={round.exportChecksum}
                copiedMessage={t(RESOURCES.Table.ChecksumCopied)}
                help={t(RESOURCES.Table.ChecksumHelp)}
              />
            )}
            {round.allocationChecksum && (
              <Fingerprint
                label={t(RESOURCES.Table.AllocationChecksum)}
                value={round.allocationChecksum}
                copiedMessage={t(RESOURCES.Table.AllocationChecksumCopied)}
                help={t(RESOURCES.Table.AllocationChecksumHelp)}
              />
            )}
            <Typography variant="subtitle2" mt={1}>
              {t(RESOURCES.Form.Rewards)}
            </Typography>
            <List dense disablePadding>
              {round.rewards.map((reward) => (
                <ListItem key={reward.order} disableGutters>
                  <ListItemText
                    primary={
                      reward.quantity && reward.quantity > 1
                        ? `${reward.quantity}× ${reward.title}`
                        : reward.title
                    }
                    secondary={reward.description}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(Resources.Common.Close)}</Button>
      </DialogActions>
    </Dialog>
  );
};
